import { Device } from "api/types/device";
import { DeviceConfiguration } from "models/devices/configuration";
import {
  MdFlashOn,
  MdGasMeter,
  MdQrCode,
  MdThermostat,
  MdWaterDrop,
  MdWaterfallChart,
} from "react-icons/md";

export const getReadings = (device: Device) => {
  const model = device.model;
  switch (model) {
    case "tag.rhw":
    case "tag.h2o":
    case "tag.wmd":
    case "lora.wmd":
    case "bulk.wmd":
      if (device.waterMeter != null || device.displayValue != null) {
        let waterMeter = device.displayValue ?? device.waterMeter;
        // Calculate usage in LITRES
        let usage = (waterMeter - device.installReading) / device.ratio;
        let odometer = device.installOdo / 10000 + usage / 1000;

        if (
          !isNaN(device.correctiveOdo) &&
          !isNaN(device.correctiveReading) &&
          device.correctiveReading >= 0 &&
          device.correctiveOdo >= 0
        ) {
          console.log("correctiveReading", device.correctiveReading);
          usage = (waterMeter - device.correctiveReading) / device.ratio;
          odometer = device.correctiveOdo / 10000 + usage / 1000;
        }

        let odo = Math.floor(odometer);
        if (isNaN(odo)) return "Not Available";

        // Pad the odometer value with leading zeros if needed
        let display = odo.toString().padStart(4, "0") + " kL";
        return display;
      } else {
        return "Not Available";
      }
    case "qrw.man":
      if (device.waterMeter != null || device.displayValue != null) {
        let waterMeter = device.displayValue ?? device.waterMeter;
        let odo = Math.floor(waterMeter);
        if (isNaN(odo)) return "Not Available";

        // Pad the odometer value with leading zeros if needed
        let display = odo.toString().padStart(4, "0");
        return display;
      } else {
        return "Not Available";
      }
    case "tag.tmp":
    case "tag.tmo":
    case "lora.tmp":
    case "lora.nanotag":
      if (device.tempMeter != null || device.displayValue != null) {
        let tempMeter = device.displayValue ?? device.tempMeter;
        let raw = tempMeter;
        if (model === "tag.tmp") raw = raw / 10;
        return `${raw.toFixed(1)} °C`;
      }
      return `Not Available`;
    case "stron.gas":
      if (device.cumulativeOdo != null || device.displayValue != null) {
        let cumulativeOdo = device.displayValue ?? device.cumulativeOdo;
        return `${cumulativeOdo.toFixed(1)} m³`;
      } else {
        return `Not Available`;
      }
    case "qrw.pre":
    case "qre.pre":
    case "qrg.pre":
      return null;
    case "tag.rhe":
    case "tag.eastron":
    case "tag.opt":
      if (device.postPaid) {
        let reading = device.displayValue || device.meterReading;
        reading =
          model === "tag.rhe"
            ? reading
            : model === "tag.opt"
              ? reading
              : reading;
        return `${reading} kWh`;
      } else if (device.userCredit) {
        return `${device.userCredit} kWh`;
      } else if (device.electricalMeter) {
        return `${device.electricalMeter} kWh`;
      } else {
        return `${device.displayValue} kWh`;
      }
    case "tag.lvl":
      if (device.level != null || device.displayValue != null) {
        let level = device.displayValue ?? device.level;
        if (device.sensorHeight >= 0 && device.overflowHeight >= 0) {
          //Get sensor offset
          let diff = device.sensorHeight - device.overflowHeight;
          //Determine water level distance from overflow
          let actualReading = level - diff;
          //Determine current tank percentage level
          let actualPercentage =
            100 - (actualReading / device.overflowHeight) * 100;
          const odo = actualPercentage.toFixed(2).toString();
          return `${odo} %`;
        }

        return `Not Available`;
      }
      return `Not Available`;

    default:
      return `Not Available`;
  }
};

export const getChartColors = (config: DeviceConfiguration) => {
  const model = config?.model || "";
  switch (model) {
    case "tag.rhw":
    case "tag.h2o":
    case "tag.wmd":
    case "lora.wmd":
    case "bulk.wmd":
      return { color0: "#1d4a64", color1: "#E1E9F8" };

    case "qrw.man":
      return { color0: "#1d4a64", color1: "#E1E9F8" };
    case "tag.tmp":
    case "tag.tmo":
    case "lora.tmp":
    case "lora.nanotag":
    case "tag.lvl":
      return { color0: "##50cfe7", color1: "#E1E9F8" };
    case "tag.eastron":
      return { color0: "##50cfe7", color1: "#E1E9F8" };
    case "qrw.pre":
    case "qre.pre":
    case "qrg.pre":
      return { color0: "#1d4a64", color1: "#E1E9F8" };
    case "tag.rhe":
      return { color0: "#1d4a64", color1: "#E1E9F8" };
    case "tag.opt":
      return {
        color0: "#1d4a64",
        color1: "#E1E9F8",
        color3: "#FFD28F",
      };
    default:
      return { color0: "#1d4a64", color1: "#E1E9F8" };
  }
};

export const getDeviceIcon = (type: string) => {
  switch (type) {
    case "water":
      return MdWaterDrop;
    case "electricity":
      return MdFlashOn;
    case "temperature":
      return MdThermostat;
    case "level":
      return MdWaterfallChart;
    case "gas":
      return MdGasMeter;
    case "qr_code":
      return MdQrCode;
    default:
      return MdWaterDrop; // Default to MdWaterDrop if the type doesn't match
  }
};

export const getDeviceBgColor = (type: string) => {
  switch (type) {
    case "water":
    case "gas":
    case "gateway":
      return "bg-background-50";
    case "gas":
      return "bg-background-300";
    case "electricity":
      return "bg-background-300";
    case "temperature":
      return "bg-background-600";
    case "level":
      return "bg-background-500";
    case "qr_code":
      return "bg-background-300";
    default:
      return "bg-background-900"; // Default to MdWaterDrop if the type doesn't match
  }
};

export const processData = (device: Device, data: any) => {
  const model = device.model;
  switch (model) {
    case "tag.rhw":
    case "tag.h2o":
    case "tag.wmd":
    case "lora.wmd":
    case "bulk.wmd":
      if (device.waterMeter != null) {
        // Calculate usage in LITRES
        let usage = (device.waterMeter - device.installReading) / device.ratio;
        let odometer = device.installReading / 10000 + usage / 1000;

        let odo = Math.floor(odometer);
        if (isNaN(odo)) return "Not Available";

        // Pad the odometer value with leading zeros if needed
        let display = odo.toString().padStart(4, "0") + " kL";
        return display;
      } else {
        return "Not Available";
      }
    case "qrw.man":
      if (device.waterMeter != null) {
        let odo = Math.floor(device.waterMeter);
        if (isNaN(odo)) return "Not Available";

        // Pad the odometer value with leading zeros if needed
        let display = odo.toString().padStart(4, "0");
        return display;
      } else {
        return "Not Available";
      }
    case "tag.tmp":
    case "tag.tmo":
    case "lora.tmp":
    case "lora.nanotag":
      if (device.tempMeter != null) {
        let raw = device.tempMeter;
        if (model === "tag.tmp") raw = raw / 10;
        return `${raw.toFixed(1)} °C`;
      }
      return `Not Available`;
    case "qrw.pre":
    case "qre.pre":
    case "qrg.pre":
      return null;
    case "tag.rhe":
      if (device.postPaid) {
        return `${device.meterReading} kWh`;
      } else {
        return `${device.userCredit} kWh`;
      }
    // return `Not Available`;
    case "stron.gas":
      return `${device.cumulativeOdo.toFixed(1)} m³`;
    case "tag.opt":
      if (device.postPaid) {
        return `${device.electricalMeter} kWh`;
      } else {
        return `${device.userCredit} kWh`;
      }
    case "tag.lvl":
      if (data != null) {
        if (device.sensorHeight >= 0 && device.overflowHeight >= 0) {
          //Get sensor offset
          let diff = device.sensorHeight - device.overflowHeight;
          //Determine water level distance from overflow
          let actualReading = data - diff;
          //Determine current tank percentage level
          let actualPercentage =
            100 - (actualReading / device.overflowHeight) * 100;

          return actualPercentage;
        }

        return `N/A`;
      }
      return `N/A`;

    default:
      return `Not Available`;
  }
};
